import { Button } from "@svea/design-system-components-react";
import {
	Grid,
	Heading,
	moneyFormatNoDecimal,
	normalizeSsn,
	P,
	Stack,
	Text,
	useText,
} from "@sveabank/cc-common";
import type { ProductSkuValue } from "@sveabank/cc-common";
import { useState } from "react";
import Card from "../../components/card";
import DotDivider from "../../components/dot-divider";
import Hide from "../../components/hide";
import Image from "../../components/image";
import { usePageContext } from "../../hooks/context";
import type { Images } from "../../images";
import { useLazyFetchCardDisplayNamesQuery } from "../../services/application";
import { isSelectedProductCreditProduct } from "../../types/product";
import type { ConfigureCardState } from "../../types/step";
import { productImages } from "../choose-package/helpers";
import CardDisplayNameModal from "./card-display-name-modal";
import GetDisplayNameForm from "./get-display-name-form";
import { useApplicationSettings } from "./hooks";
import SelectedCardForm from "./selected-card-form";

type Props = {
	card: ConfigureCardState;
};

const CardSettingItem = ({ card }: Props) => {
	const [t, texts] = useText();
	const {
		selectedPackage: { products: selectedProducts },
	} = usePageContext(useApplicationSettings);

	const getCardProductSku = (card: ConfigureCardState): ProductSkuValue | undefined =>
		selectedProducts.find((p) => p.productId === card.productId)?.sku;

	const getCardTitle = (card: ConfigureCardState) => {
		const sku = getCardProductSku(card);
		return sku ? t(texts.addons[sku].title) : "";
	};

	const [fetchCardNames, { data: cardNames }] = useLazyFetchCardDisplayNamesQuery();

	const [isOpen, setIsOpen] = useState(false);
	const [isEdit, setIsEdit] = useState(false);

	const [ssn, setSsn] = useState(card.nationalId);
	const [displayName, setDisplayName] = useState(card.displayName);

	const handleOnSave = (name: string) => {
		setDisplayName(name);
		setIsOpen(false);
	};

	const submitSsn = async (values: { ssn: string }) => {
		const normalizedSsn = normalizeSsn(values.ssn);
		await fetchCardNames(normalizedSsn).unwrap();
		setSsn(normalizedSsn);
		setIsOpen(true);
	};

	const handleCardName = async (nationalId: string) => {
		await fetchCardNames(nationalId).unwrap();
		setIsOpen(true);
	};

	const handleEditCardOwner = () => {
		setDisplayName(undefined);
		setSsn(undefined);
	};

	const productTitle = getCardTitle(card);

	const getCardImage = (card: ConfigureCardState): keyof typeof Images => {
		const sku = getCardProductSku(card);
		const producImage = sku && productImages[sku] ? productImages[sku] : "DEBITCARD";

		return producImage;
	};

	const cardProductInfo = selectedProducts?.find((product) => product.productId === card.productId);
	const cardLimit =
		cardProductInfo &&
		isSelectedProductCreditProduct(cardProductInfo) &&
		`${t(texts.applicationSettings.cardSettings.creditLimit)}: ${moneyFormatNoDecimal(
			Number(cardProductInfo.chosenCredit.amount),
			true,
		)}`;

	return (
		<div key={card.productId}>
			{card.status === "NotCompleted" && (
				<div className="tablet:mb-4 mb-0">
					<Heading.H4>{productTitle}</Heading.H4>
					{cardLimit}
				</div>
			)}

			{card.status === "Completed" && !isEdit && (
				<Card classNames="bg-background-secondary" boxShadow={false}>
					<Stack gap={2} direction="row">
						<Hide to="tablet">
							<DotDivider size={1} color="text-primary">
								<div className="font-bold">{productTitle}</div>
								{cardLimit}
								<div>{displayName || card.displayName}</div>
							</DotDivider>
						</Hide>
						<Hide from="tablet">
							<Stack gap={1}>
								<P>
									<Text bold className="mr-4">
										{productTitle}
									</Text>
									{cardLimit}
								</P>
								<div>{displayName || card.displayName}</div>
							</Stack>
						</Hide>
					</Stack>

					<Button
						variant="icon"
						aria-label={t(texts.applicationSettings.buttonText.editCard)}
						onPress={() => {
							setIsEdit(true);
						}}
						icon="pencil"
					/>
				</Card>
			)}
			{(card.status === "NotCompleted" || isEdit) && (
				<Grid>
					<Grid.Col span={{ base: 12, tablet: 2 }}>
						<Hide to="tablet">
							<Image altText="Kort" imageSrc={getCardImage(card)} />
						</Hide>
					</Grid.Col>
					<Grid.Col span={{ base: 12, tablet: 10 }}>
						{displayName && ssn ? (
							<SelectedCardForm
								onEditName={async () => {
									await handleCardName(ssn);
								}}
								onEditCardOwner={handleEditCardOwner}
								setIsEdit={setIsEdit}
								displayName={displayName}
								ssn={ssn}
								card={card}
							/>
						) : (
							<GetDisplayNameForm onSubmit={submitSsn} />
						)}
					</Grid.Col>
				</Grid>
			)}
			{cardNames && (
				<CardDisplayNameModal
					open={isOpen}
					setOpen={setIsOpen}
					cardNames={cardNames}
					onSave={handleOnSave}
					selectedCardName={card.displayName}
				/>
			)}
		</div>
	);
};

export default CardSettingItem;
